import React from 'react';
import { CalendarPlus, FileText, Plus, Upload } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { useLocale } from '../contexts/LocaleContext';
import { ButtonTooltip, TooltipProvider } from './ui/tooltip';

interface AddTimelineEventDataMenuProps {
  onAddEvent: () => void;
  onImport: () => void;
  onFromDocument: () => void;
  fromDocumentDisabled?: boolean;
  fromDocumentLoading?: boolean;
}

export const AddTimelineEventDataMenu: React.FC<AddTimelineEventDataMenuProps> = ({
  onAddEvent,
  onImport,
  onFromDocument,
  fromDocumentDisabled = false,
  fromDocumentLoading = false,
}) => {
  const { t } = useLocale();

  return (
    <DropdownMenu>
      <TooltipProvider delayDuration={0}>
        <ButtonTooltip tooltip={t('timeline_new_event_data_tooltip')} side="bottom">
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="inverse" className="group h-auto min-h-8">
              <Plus className="h-4 w-4 transition-transform duration-500 ease-in-out group-hover:rotate-[360deg]" />
              {t('timeline_new_event_data')}
            </Button>
          </DropdownMenuTrigger>
        </ButtonTooltip>
      </TooltipProvider>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onClick={onAddEvent}
          title={t('add_event_tooltip')}
          className="group/add-event"
        >
          <CalendarPlus className="mr-2 h-4 w-4 transition-transform duration-300 group-hover/add-event:scale-110" />
          {t('add_event')}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={onImport}
          title={t('timeline_import_tooltip')}
          className="group/import"
        >
          <Upload className="mr-2 h-4 w-4 transition-transform duration-300 group-hover/import:-translate-y-0.5" />
          {t('timeline_import')}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={onFromDocument}
          disabled={fromDocumentDisabled || fromDocumentLoading}
          title={t('timeline_auto_extract_tooltip')}
          className="group/from-document"
        >
          <FileText className="mr-2 h-4 w-4 transition-transform duration-300 group-hover/from-document:scale-110" />
          {fromDocumentLoading ? t('analyzing') : t('timeline_auto_extract')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
